import { type FC, useCallback } from "react";

import { serverEndSession } from "api/api-server-actions";
import { useVirtualClassActions } from "components/av/virtual-class-utils";
import { ClassSessionCategory } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Menu, MenuDivider, MenuItem, SubMenu } from "mds/components/Menu";
import { PillButton } from "mds/components/PillButton";
import {
  ChatWithLinesIcon,
  ChevronDownIcon,
  CopySendArrowIcon,
  HandRaiseOutlineIcon,
  InPersonIcon,
  PersonGroupIcon,
  ReactionIcon,
  RepeatIcon,
  VideoIcon,
} from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  selectActiveClassSessionCategory,
  selectActiveClassSessionId,
  selectCurrentClassUserId,
  selectCurrentClassUserIsInPersonClass,
  selectCurrentSectionAvEnabled,
  selectCurrentSectionId,
  selectIsPracticeClassSession,
} from "store/selectors";
import { useAv } from "utils/av";
import { useClassActions } from "utils/class-utils";
import { useJoinAsSectionAssistant } from "utils/hooks/use-join-as-section-assistant";
import { isAVEnabled } from "utils/waffle-flags";

export const SessionButton: FC = () => {
  const { canAuthorSection } = useCapabilities();
  const classSessionId = useAppSelector(selectActiveClassSessionId);
  const classSessionCategory = useAppSelector(selectActiveClassSessionCategory);
  const sectionId = useAppSelector(selectCurrentSectionId);
  const currentClassUserId = useAppSelector(selectCurrentClassUserId);
  const isInInPersonClass = useAppSelector(selectCurrentClassUserIsInPersonClass);
  const isPracticeClassSession = useAppSelector(selectIsPracticeClassSession);
  const avEnabled = useAppSelector(selectCurrentSectionAvEnabled) && isAVEnabled();

  const { isInVirtualClass, isAvActiveInAnotherTab, isAvSupportedInBrowser } = useAv();
  const isAvSupportedAndEnabled = isAvSupportedInBrowser && avEnabled;
  const { joinClass, leaveClass } = useClassActions();
  const { showAvCheckToJoinVirtualClass, stopVirtualClass } = useVirtualClassActions();
  const { joinAsSectionAssistantIfNew } = useJoinAsSectionAssistant();

  const startInPersonSession = async () => {
    const { classUserId } = await joinAsSectionAssistantIfNew();

    await storeApi.class_sessions.create({
      section_id: sectionId!,
      category: ClassSessionCategory.IN_PERSON,
    });

    joinClass(classUserId);
  };

  const endSession = () => {
    if (!classSessionId) return;
    if (classSessionCategory === ClassSessionCategory.IN_PERSON) {
      serverEndSession(classSessionId);
    } else {
      stopVirtualClass();
    }
  };

  const startPracticeInPersonSession = async () => {
    if (!sectionId) return;
    await storeApi.class_sessions.create({
      section_id: sectionId,
      category: ClassSessionCategory.IN_PERSON,
      is_practice: true,
    });

    joinClass();
  };

  const onJoinSession = useCallback(() => {
    if (classSessionCategory === ClassSessionCategory.IN_PERSON) {
      joinClass();
    } else {
      showAvCheckToJoinVirtualClass();
    }
  }, [classSessionCategory, joinClass, showAvCheckToJoinVirtualClass]);

  if (
    !sectionId ||
    isInVirtualClass ||
    isAvActiveInAnotherTab ||
    (!canAuthorSection && !classSessionCategory) ||
    (!canAuthorSection && !currentClassUserId) ||
    (classSessionCategory === ClassSessionCategory.VIRTUAL && !isAvSupportedAndEnabled)
  ) {
    return null;
  }

  if (canAuthorSection && classSessionCategory) {
    return (
      <PillButton
        className="gap-2"
        eventName="End Current Session"
        kind="red"
        size="m"
        onClick={endSession}
      >
        <CopySendArrowIcon />
        {isPracticeClassSession
          ? t("section.start_session_menu.end_practice")
          : t("section.start_session_menu.end_session")}
      </PillButton>
    );
  }

  if (!canAuthorSection && isInInPersonClass) {
    return (
      <PillButton
        className="gap-2"
        eventName="Leave Session"
        kind="red"
        size="m"
        onClick={leaveClass}
      >
        <CopySendArrowIcon />
        {t("section.start_session_menu.leave_session")}
      </PillButton>
    );
  }

  const MenuButton = (
    <PillButton className="gap-2" eventName="Start a Session - Open Menu" kind="dark-gray" size="m">
      {t("section.start_session_menu.title")}
      <ChevronDownIcon />
    </PillButton>
  );

  const iconCircleClasses = "flex size-6 items-center justify-center rounded-full bg-black-tint-97";

  if (canAuthorSection) {
    return (
      <Menu align="end" direction="bottom" limitItemHeight={false} menuButton={MenuButton}>
        <MenuItem
          aria-label={t("section.start_session_menu.in_person")}
          eventName="Start a Session - In-Person"
          onClick={startInPersonSession}
        >
          <div className="p-1">
            <div className="flex justify-between">
              <IconText
                iconStart={<InPersonIcon className="text-blue" />}
                text={t("section.start_session_menu.in_person")}
              />
              <div className="flex items-center gap-1">
                <div className={iconCircleClasses}>
                  <ReactionIcon />
                </div>

                <div className={iconCircleClasses}>
                  <ChatWithLinesIcon />
                </div>

                <div className={iconCircleClasses}>
                  <HandRaiseOutlineIcon />
                </div>
              </div>
            </div>

            <div className="mt-1.5 max-w-[290px] text-xs text-black-tint-40">
              {t("section.start_session_menu.in_person_description")}
            </div>
          </div>
        </MenuItem>

        {isAvSupportedAndEnabled && (
          <>
            <MenuDivider />
            <MenuItem
              aria-label={t("section.start_session_menu.virtual_class")}
              eventName="Start a Session - Virtual"
              onClick={() => showAvCheckToJoinVirtualClass()}
            >
              <div className="p-1">
                <div className="flex justify-between">
                  <IconText
                    iconStart={<VideoIcon className="text-blue" />}
                    text={t("section.start_session_menu.virtual")}
                  />

                  <div className="flex items-center gap-1">
                    <div className={iconCircleClasses}>
                      <VideoIcon />
                    </div>

                    <div className={iconCircleClasses}>
                      <PersonGroupIcon />
                    </div>

                    <div className={iconCircleClasses}>
                      <ReactionIcon />
                    </div>

                    <div className={iconCircleClasses}>
                      <ChatWithLinesIcon />
                    </div>

                    <div className={iconCircleClasses}>
                      <HandRaiseOutlineIcon />
                    </div>
                  </div>
                </div>

                <div className="mt-1.5 max-w-[290px] text-xs text-black-tint-40">
                  {t("section.start_session_menu.virtual_description")}
                </div>
              </div>
            </MenuItem>
          </>
        )}
        <MenuDivider />
        <SubMenu
          label={
            <IconText
              iconStart={<RepeatIcon className="text-blue" />}
              text={t("section.start_session_menu.practice")}
            />
          }
          withArrow
        >
          <MenuItem eventName="Practice - In-Person" onClick={startPracticeInPersonSession}>
            <IconText
              iconStart={<InPersonIcon />}
              text={t("section.start_session_menu.in_person")}
            />
          </MenuItem>
          {isAvSupportedAndEnabled && (
            <>
              <MenuDivider />
              <MenuItem
                eventName="Practice - Virtual"
                onClick={() => showAvCheckToJoinVirtualClass(true)}
              >
                <IconText
                  iconStart={<VideoIcon />}
                  text={t("section.start_session_menu.virtual")}
                />
              </MenuItem>
            </>
          )}
        </SubMenu>
      </Menu>
    );
  }

  return !isPracticeClassSession ? (
    <PillButton
      className="gap-2"
      eventName="Join Session"
      kind="blue"
      size="m"
      onClick={onJoinSession}
    >
      {classSessionCategory === ClassSessionCategory.IN_PERSON ? <InPersonIcon /> : <VideoIcon />}
      {t("footer.join_session")}
    </PillButton>
  ) : null;
};
